import { QueryResult, useQuery } from "@apollo/client";
import { useFormikContext } from "formik";
import { ChangeEvent, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
  Switch,
  FormControlLabel,
  FormGroup,
  Card,
  CardContent
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import MultiSelectTable from "src/components/MultiSelectTable";
import FormVanSearchField from "src/components/form/FormVanSearchField";
import { VanSearchFieldValue } from "src/components/form/FormVanSearchField/types";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import {
  getCampaignVanSurveyQuestionData,
  getCampaignVanSurveyQuestionDataVariables
} from "src/graphql/generated/getCampaignVanSurveyQuestionData";
import {
  QuestionType,
  VanProxySearchType
} from "src/graphql/generated/globalTypes";

import {
  editorStateToPlainText,
  plainTextToInitialEditorState
} from "./TextEditor/serialization";
import { GET_QUESTION_DATA } from "./queries";
import { FormAnswer, FormQuestion } from "./types";

const useStyles = makeStyles(() => ({
  content: {
    width: "80vw",
    maxWidth: "600px"
  },
  cardContent: {
    padding: "18px",
    "&:last-child": {
      paddingBottom: "18px"
    }
  },
  toggleGroup: {
    marginBottom: "24px"
  }
}));

function SurveyQuestionImportSettings({
  queryData,
  selectedQuestion,
  importName,
  setImportName,
  nameIsBlank,
  importScript,
  setImportScript,
  scriptIsBlank,
  importAnswers,
  setImportAnswers,
  answersAreBlank,
  maxAnswers
}: {
  selectedQuestion: VanSearchFieldValue | null;
  queryData: QueryResult<getCampaignVanSurveyQuestionData>;
  importName: boolean;
  setImportName: (val: boolean) => void;
  nameIsBlank: boolean;
  importScript: boolean;
  setImportScript: (val: boolean) => void;
  scriptIsBlank: boolean;
  importAnswers: Set<number> | null;
  setImportAnswers: (val: Set<number> | null) => void;
  answersAreBlank: boolean;
  maxAnswers: number;
}): JSX.Element | null {
  const styles = useStyles();

  if (!selectedQuestion) {
    return null;
  }

  const { loading, error, data } = queryData;
  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  const surveyQuestion = data!.campaign.vanIntegration!.surveyQuestion;

  const selectAllImportQuestion = new Set(
    surveyQuestion.responses.map(d => d["surveyResponseId"])
  );

  return (
    <>
      <FormGroup className={styles.toggleGroup}>
        <FormControlLabel
          control={
            <Switch
              checked={importName}
              onChange={evt => setImportName(evt.target.checked)}
              color="primary"
            />
          }
          label={`Import question name${
            nameIsBlank ? "" : " (existing question name will be overwritten)"
          }?`}
        />
        {importName && (
          <Card>
            <CardContent className={styles.cardContent}>
              {surveyQuestion.name}
            </CardContent>
          </Card>
        )}
      </FormGroup>
      <FormGroup className={styles.toggleGroup}>
        <FormControlLabel
          control={
            <Switch
              checked={importScript}
              onChange={evt => setImportScript(evt.target.checked)}
              color="primary"
            />
          }
          label={`Import question script${
            scriptIsBlank ? "" : " (existing script will be overwritten)"
          }?`}
        />
        {importScript && (
          <Card>
            <CardContent className={styles.cardContent}>
              {surveyQuestion.scriptQuestion}
            </CardContent>
          </Card>
        )}
      </FormGroup>
      <FormGroup className={styles.toggleGroup}>
        <FormControlLabel
          control={
            <Switch
              checked={!!importAnswers}
              onChange={evt =>
                setImportAnswers(
                  evt.target.checked ? selectAllImportQuestion : null
                )
              }
              color="primary"
            />
          }
          label={`Import question responses${
            answersAreBlank ? "" : " (existing responses will be overwritten)"
          }?`}
        />
        {importAnswers && (
          <>
            <MultiSelectTable
              data={surveyQuestion.responses}
              columns={[
                {
                  header: "Question Response",
                  key: "name",
                  render: (name: string) => name
                }
              ]}
              containerStyle={{ maxHeight: 500 }}
              onChangeSelectedRows={setImportAnswers}
              selectedRows={importAnswers}
              primaryKey="surveyResponseId"
              disableTableHeader
            />
            {importAnswers.size > maxAnswers && (
              <Alert severity="error">
                You can only add {maxAnswers} answers for each question.
              </Alert>
            )}
          </>
        )}
      </FormGroup>
    </>
  );
}

export default function VANQuestionImport({
  name,
  open,
  onClose,
  question,
  campaign,
  maxAnswers
}: {
  name: string;
  open: boolean;
  onClose: () => void;
  question: FormQuestion;
  campaign: getCampaign_campaign;
  maxAnswers: number;
}): JSX.Element {
  const { setFieldValue } = useFormikContext<unknown>();
  const classes = useStyles();
  const [selectedQuestion, setSelectedQuestion] =
    useState<VanSearchFieldValue | null>(null);

  const [importName, setImportName] = useState(false);
  const [importScript, setImportScript] = useState(false);
  const [importAnswers, setImportAnswers] = useState<Set<number> | null>(null);

  const nameIsBlank = question.name.trim() === "";
  const scriptIsBlank = editorStateToPlainText(question.script).trim() === "";
  const answersAreBlank = question.answers.length === 0;

  const queryData = useQuery<
    getCampaignVanSurveyQuestionData,
    getCampaignVanSurveyQuestionDataVariables
  >(GET_QUESTION_DATA, {
    variables: {
      campaignId: campaign.id,
      vanSurveyQuestionId: selectedQuestion?.id as number
    },
    skip: !selectedQuestion,
    onCompleted: data => {
      if (answersAreBlank) {
        const surveyQuestion = data!.campaign.vanIntegration!.surveyQuestion;

        const selectAllImportQuestion = new Set(
          surveyQuestion.responses.map(d => d["surveyResponseId"])
        );

        setImportAnswers(selectAllImportQuestion);
      }
    }
  });

  function handleClose() {
    setSelectedQuestion(null);
    setImportName(false);
    setImportScript(false);
    setImportAnswers(null);
    onClose();
  }

  function handlePickQuestion(
    evt: ChangeEvent<any>,
    question: VanSearchFieldValue | null
  ) {
    setSelectedQuestion(question);

    setImportName(nameIsBlank);
    setImportScript(scriptIsBlank);
    setImportAnswers(answersAreBlank ? new Set() : null);
  }

  function handleSubmit() {
    const surveyQuestion =
      queryData.data?.campaign.vanIntegration?.surveyQuestion;
    if (!surveyQuestion) {
      return;
    }

    setFieldValue(
      `${name}.vanIntegrationSettings.surveyQuestionId`,
      selectedQuestion?.id
    );

    if (importName) {
      setFieldValue(`${name}.name`, surveyQuestion.name);
    }

    if (importScript) {
      setFieldValue(
        `${name}.script`,
        plainTextToInitialEditorState(
          surveyQuestion.scriptQuestion,
          campaign.calleeFields
        )
      );
    }

    if (importAnswers) {
      const newAnswers: FormAnswer[] = surveyQuestion.responses
        .filter(r => importAnswers.has(r.surveyResponseId))
        .map(surveyQuestionResponse => ({
          __typename: "CampaignAnswer",
          key: uuidv4(), // placeholder, filled in when we send to the server
          value: surveyQuestionResponse.name,
          next: null,
          disposition: null,
          vanIntegrationSettings: {
            __typename: "CampaignAnswerVanIntegrationSettings",
            activistCodeId: null,
            activistCodeData: null,
            surveyResponseId: surveyQuestionResponse.surveyResponseId,
            surveyResponseData: surveyQuestionResponse,
            resultCodeId: null,
            resultCodeData: null
          },
          patchThruConfig: null
        }));

      setFieldValue(`${name}.answers`, newAnswers);
      setFieldValue(`${name}.questionType`, QuestionType.SINGLE_CHOICE);
    }

    setFieldValue(
      `${name}.vanIntegrationSettings.surveyQuestionData`,
      surveyQuestion
    );

    handleClose();
  }

  const canSave =
    !!selectedQuestion &&
    (!importAnswers || importAnswers.size <= maxAnswers) &&
    !!queryData.data?.campaign.vanIntegration?.surveyQuestion;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="van-canvass-result-dialog-title"
      aria-describedby="van-canvass-result-dialog-description"
    >
      <DialogTitle id="van-canvass-result-dialog-title">
        Import NGP VAN Survey Question
      </DialogTitle>
      <DialogContent className={classes.content}>
        <FormVanSearchField
          fullWidth
          label="NGP VAN Survey Question"
          value={selectedQuestion}
          onChange={handlePickQuestion}
          campaignId={campaign.id}
          searchType={VanProxySearchType.SURVEY_QUESTION}
        />
        <SurveyQuestionImportSettings
          selectedQuestion={selectedQuestion}
          queryData={queryData}
          importName={importName}
          setImportName={setImportName}
          nameIsBlank={nameIsBlank}
          importScript={importScript}
          setImportScript={setImportScript}
          scriptIsBlank={scriptIsBlank}
          importAnswers={importAnswers}
          setImportAnswers={setImportAnswers}
          answersAreBlank={answersAreBlank}
          maxAnswers={maxAnswers}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!canSave}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
